import React, { useMemo } from 'react';
import { BottomSheet } from './BottomSheet';
import { formatGamePoint, reduce, type MatchState, type Side } from '../../../../services/tennis/scoring';
import type { TennisMatchFormat } from '../../../../data/tennisMatchFormats';
import type { QueuedPoint } from '../../../../services/tennis/offlineQueue';
import type { PointEndReason } from '../../../../types';

const STRIP_LENGTH = 10;

const sideColor = (side: Side) => (side === 'us' ? 'bg-primary' : 'bg-slate-600 dark:bg-slate-500');

/** Last-10-points strip under the scoreboard. Tapping it opens the full log. */
export const PointLogStrip: React.FC<{ points: QueuedPoint[]; onOpen: () => void }> = ({ points, onOpen }) => {
  const recent = points.slice(-STRIP_LENGTH);
  const slots: Array<QueuedPoint | null> = [...Array(STRIP_LENGTH - recent.length).fill(null), ...recent];

  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label="Open point log"
      className="w-full flex items-center gap-2 min-h-[28px] text-[10px] font-bold tracking-wider text-gray-400 dark:text-gray-500"
    >
      <span>LAST {STRIP_LENGTH}</span>
      <span className="flex-1 flex gap-[3px]">
        {slots.map((p, i) => (
          <span
            key={p?.client_id ?? `empty-${i}`}
            className={`flex-1 h-3 rounded-[3px] ${p ? sideColor(p.won_by) : 'bg-gray-200 dark:bg-gray-800'}`}
          />
        ))}
      </span>
      <span className="text-primary">LOG</span>
    </button>
  );
};

const REASON_LABEL: Record<PointEndReason, string> = {
  winner: 'Winner',
  ace: 'Ace',
  unforced: 'Unforced error',
  forced: 'Forced error',
  double_fault: 'Double fault',
};

function gameScoreLabel(state: MatchState, noAd: boolean): string {
  if (state.winner) return 'Match';
  if (!state.game) return '';
  const { ours, theirs } = state.game.isTB
    ? { ours: String(state.game.points.ours), theirs: String(state.game.points.theirs) }
    : formatGamePoint(state.game.points, noAd);
  return `${ours}–${theirs}`;
}

interface PointLogSheetProps {
  open: boolean;
  onClose: () => void;
  points: QueuedPoint[];
  format: TennisMatchFormat;
  isDoubles: boolean;
  ourName: string;
  opponentName: string;
  ourPlayers: Array<{ id: string; firstName: string }>;
}

/**
 * Full point-by-point log, newest first, grouped by game. Score shown after
 * each point is from our side's perspective; a point that ends a game shows
 * the game result instead.
 */
export const PointLogSheet: React.FC<PointLogSheetProps> = ({
  open,
  onClose,
  points,
  format,
  isDoubles,
  ourName,
  opponentName,
  ourPlayers,
}) => {
  const rows = useMemo(() => {
    if (!open) return [];
    const engine = points.map((p) => ({ winner: p.won_by }));
    return points
      .map((p, i) => {
        const before = reduce(format, engine.slice(0, i), { isDoubles });
        const after = reduce(format, engine.slice(0, i + 1), { isDoubles });
        const gameEnded =
          !after.winner &&
          (after.sets.length !== before.sets.length ||
            (after.currentSet?.ourGames ?? 0) + (after.currentSet?.theirGames ?? 0) !==
              (before.currentSet?.ourGames ?? 0) + (before.currentSet?.theirGames ?? 0));
        const playerName = ourPlayers.find((pl) => pl.id === p.winning_player_id)?.firstName;
        const tags = [p.end_reason ? REASON_LABEL[p.end_reason] : null, p.shot_type, p.error_spot ? `${p.error_spot}` : null]
          .filter(Boolean)
          .join(' · ');
        return {
          point: p,
          group: before.game?.isTB
            ? `Set ${before.sets.length + 1} · Tiebreak`
            : `Set ${before.sets.length + 1} · Game ${(before.currentSet?.ourGames ?? 0) + (before.currentSet?.theirGames ?? 0) + 1}`,
          who: playerName ?? (p.won_by === 'us' ? ourName : opponentName),
          tags,
          score: gameEnded ? 'Game' : gameScoreLabel(after, format.noAd),
        };
      })
      .reverse();
  }, [open, points, format, isDoubles, ourName, opponentName, ourPlayers]);

  return (
    <BottomSheet open={open} title="Point log" onClose={onClose}>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">No points yet. Tap a team's side of the court to score.</p>
      ) : (
        <ol className="space-y-0.5">
          {rows.map((r, i) => (
            <React.Fragment key={r.point.client_id}>
              {(i === 0 || rows[i - 1].group !== r.group) && (
                <li className="pt-3 pb-1 text-[10px] font-extrabold tracking-widest uppercase text-gray-400 dark:text-gray-500 first:pt-0">
                  {r.group}
                </li>
              )}
              <li className="flex items-center gap-3 py-2 border-b border-gray-100 dark:border-gray-800">
                <span className={`w-1.5 self-stretch rounded-full ${sideColor(r.point.won_by)}`} aria-hidden />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{r.who}</p>
                  {r.tags && <p className="text-xs text-gray-500 dark:text-gray-400 capitalize truncate">{r.tags}</p>}
                </div>
                <span className="text-sm font-bold tabular-nums text-gray-700 dark:text-gray-300">{r.score}</span>
              </li>
            </React.Fragment>
          ))}
        </ol>
      )}
      <p className="mt-3 text-[11px] text-gray-400 dark:text-gray-500">
        Scores shown as {ourName} – {opponentName}.
      </p>
    </BottomSheet>
  );
};
